import { facadeApiV3 as api } from "./baseApiV3";
const injectedRtkApi = api.injectEndpoints({
  endpoints: (build) => ({
    getTours: build.query<GetToursApiResponse, GetToursApiArg>({
      query: (queryArg) => ({
        url: `/tour`,
        params: {
          pageIndex: queryArg.pageIndex,
          pageSize: queryArg.pageSize,
          assetId: queryArg.assetId,
        },
      }),
    }),
    createTour: build.mutation<CreateTourApiResponse, CreateTourApiArg>({
      query: (queryArg) => ({ url: `/tour`, method: "POST", body: queryArg }),
    }),
    getTour: build.query<GetTourApiResponse, GetTourApiArg>({
      query: (queryArg) => ({ url: `/tour/${queryArg}` }),
    }),
    updateTour: build.mutation<UpdateTourApiResponse, UpdateTourApiArg>({
      query: (queryArg) => ({
        url: `/tour/${queryArg.id}`,
        method: "PUT",
        body: queryArg.tourRequest,
      }),
    }),
    canceledTour: build.mutation<CanceledTourApiResponse, CanceledTourApiArg>({
      query: (queryArg) => ({ url: `/tour/${queryArg}`, method: "DELETE" }),
    }),
    sendTourToVehicle: build.mutation<
      SendTourToVehicleApiResponse,
      SendTourToVehicleApiArg
    >({
      query: (queryArg) => ({
        url: `/tour/${queryArg.id}/itinerary/${queryArg.itineraryId}/send-to-vehicle`,
        method: "POST",
      }),
    }),
    sendTourToOptimizer: build.mutation<
      SendTourToOptimizerApiResponse,
      SendTourToOptimizerApiArg
    >({
      query: (queryArg) => ({
        url: `/tour/${queryArg.id}/itinerary/${queryArg.itineraryId}/send-to-optimizer`,
        method: "POST",
      }),
    }),
    fetchTourItineraries: build.query<
      FetchTourItinerariesApiResponse,
      FetchTourItinerariesApiArg
    >({
      query: (queryArg) => ({
        url: `/tour/${queryArg.id}/itineraries`,
        params: {
          status: queryArg.status,
        },
      }),
    }),
    getMmtMessages: build.query<
      GetMmtMessagesApiResponse,
      GetMmtMessagesApiArg
    >({
      query: (queryArg) => ({ url: `/tour/${queryArg}/mmt-messages` }),
    }),
    getChargingStationMetadataList: build.mutation<
      GetChargingStationMetadataListApiResponse,
      GetChargingStationMetadataListApiArg
    >({
      query: (queryArg) => ({
        url: `/charging-stations/metadata`,
        method: "POST",
        body: queryArg,
      }),
    }),
    getChargingStation: build.query<
      GetChargingStationApiResponse,
      GetChargingStationApiArg
    >({
      query: (queryArg) => ({ url: `/charging-stations/details/${queryArg}` }),
    }),
    getVehicleProfileList: build.query<
      GetVehicleProfileListApiResponse,
      GetVehicleProfileListApiArg
    >({
      query: () => ({ url: `/profiles` }),
    }),
    getVehicleProfileDetails: build.query<
      GetVehicleProfileDetailsApiResponse,
      GetVehicleProfileDetailsApiArg
    >({
      query: (queryArg) => ({ url: `/profiles/${queryArg}` }),
    }),
    getPermittedAssets: build.query<
      GetPermittedAssetsApiResponse,
      GetPermittedAssetsApiArg
    >({
      query: () => ({ url: `/vehicles` }),
    }),
    getAssetData: build.query<GetAssetDataApiResponse, GetAssetDataApiArg>({
      query: (queryArg) => ({ url: `/vehicles/${queryArg}` }),
    }),
    generateIsoline: build.query<
      GenerateIsolineApiResponse,
      GenerateIsolineApiArg
    >({
      query: (queryArg) => ({
        url: `/vehicles/${queryArg.id}/isoline`,
        params: {
          amount: queryArg.amount,
          weight: queryArg.weight,
          isolineMode: queryArg.isolineMode,
          origin: queryArg.origin,
        },
      }),
    }),
    sendMmtMessage: build.mutation<
      SendMmtMessageApiResponse,
      SendMmtMessageApiArg
    >({
      query: (queryArg) => ({
        url: `/mmt/messages`,
        method: "POST",
        body: queryArg,
      }),
    }),
    getAssetSubscriptionState: build.query<
      GetAssetSubscriptionStateApiResponse,
      GetAssetSubscriptionStateApiArg
    >({
      query: (queryArg) => ({ url: `/vehicles/${queryArg}/subscription` }),
    }),
  }),
  overrideExisting: false,
});
export { injectedRtkApi as facadeApiV3 };
export type GetToursApiResponse = /** status 200 OK */ TourListResponse;
export type GetToursApiArg = {
  /** Page index for cursor pagination */
  pageIndex?: number;
  /** Number of items per page (min = 1, max = 1000) */
  pageSize?: number;
  /** Vehicle Id */
  assetId?: string;
};
export type CreateTourApiResponse =
  /** status 201 OK */ TourItinerariesResponse;
export type CreateTourApiArg = TourRequest;
export type GetTourApiResponse = /** status 200 OK */ TourResponse;
export type GetTourApiArg = string;
export type UpdateTourApiResponse =
  /** status 200 OK */ TourItinerariesResponse;
export type UpdateTourApiArg = {
  id: string;
  tourRequest: TourRequest;
};
export type CanceledTourApiResponse = unknown;
export type CanceledTourApiArg = string;
export type SendTourToVehicleApiResponse = unknown;
export type SendTourToVehicleApiArg = {
  id: string;
  /** Tour's Itinerary id */
  itineraryId: string;
};
export type SendTourToOptimizerApiResponse = unknown;
export type SendTourToOptimizerApiArg = {
  id: string;
  /** Tour's Itinerary id */
  itineraryId: string;
};
export type FetchTourItinerariesApiResponse =
  /** status 200 OK */ ItinerariesResponse;
export type FetchTourItinerariesApiArg = {
  id: string;
  /** Filter to select Itinerary's status */
  status?: ItineraryStatus;
};
export type GetMmtMessagesApiResponse =
  /** status 200 OK */ MmtMessagesResponse;
export type GetMmtMessagesApiArg = string;
export type GetChargingStationMetadataListApiResponse =
  /** status 200 Successful response */ ChargingStationMetadataListResponse;
export type GetChargingStationMetadataListApiArg =
  ChargingStationMetadataRequest;
export type GetChargingStationApiResponse =
  /** status 200 Successful response */ ChargingStation;
export type GetChargingStationApiArg = string;
export type GetVehicleProfileListApiResponse =
  /** status 200 Returns a list of all available vehicle profiles */ VehicleProfileListItem[];
export type GetVehicleProfileListApiArg = void;
export type GetVehicleProfileDetailsApiResponse =
  /** status 200 Returns a vehicle profile */ VehicleProfileDetails;
export type GetVehicleProfileDetailsApiArg = string;
export type GetPermittedAssetsApiResponse =
  /** status 200 Returns permitted assets */ PermittedAssetsResponse;
export type GetPermittedAssetsApiArg = void;
export type GetAssetDataApiResponse = /** status 200 OK */ AssetDataResponse;
export type GetAssetDataApiArg = string;
export type GenerateIsolineApiResponse =
  /** status 200 Isoline Range calculated */ IsolineResponse;
export type GenerateIsolineApiArg = {
  id: string;
  /** Maximum values per isoline request type (should be positive)
    DISTANCE: 1000000 (in meters)
    TIME: 32400 (in seconds)
    CONSUMPTION: EV (228060 in Watt-hour)
     */
  amount: number;
  /** Vehicle total weight in Kg (e.g. 30000) */
  weight: number;
  /** Isoline Calculation Mode
   */
  isolineMode: IsolineMode;
  /** Format: `<lat>,<lng>`
    - `lat`: Latitude
    - `lng`: Longitude
     */
  origin: string;
};
export type SendMmtMessageApiResponse = unknown;
export type SendMmtMessageApiArg = MqttMoMessage;
export type GetAssetSubscriptionStateApiResponse =
  /** status 200 Returns current Subscription state */ AssetSubscriptionState;
export type GetAssetSubscriptionStateApiArg = string;
export type Address = {
  name?: string;
  street?: string;
  number?: string;
  city: string;
  region?: string;
  countryCode: string;
  postalCode: string;
};
export type TimelineEventSummary = {
  estimatedDay?: string;
  expectedBatteryLevelSoC?: number;
};
export type ItineraryDetails = {
  /** Itinerary's duration in seconds */
  totalDuration?: number;
  /** Itinerary's total distance in meters */
  totalDistance?: number;
  arrivalEvent?: TimelineEventSummary;
  departureEvent?: TimelineEventSummary;
};
export type TourSummaryItem = {
  tourId?: string;
  status?: TourStatus;
  createAt?: string;
  lastUpdate?: string;
  arrivalAddress?: Address;
  departureAddress?: Address;
  itinerarySummary?: ItineraryDetails;
};
export type TourListResponse = {
  totalCount?: number;
  tours?: TourSummaryItem[];
};
export type ErrorMessage = {
  error_code: ErrorMessageErrorCode;
  /** Additional details about the error */
  message?: string;
};
export type Coordinates = {
  latitude: number;
  longitude: number;
};
export type TourDeparture = {
  coordinates: Coordinates;
  name: string;
  address: Address;
  desiredDepartureTime?: string;
  initialCargoWeight: number;
  temperatureForecast?: number;
};
export type TourArrival = {
  coordinates: Coordinates;
  name: string;
  address: Address;
  desiredArrivalTime?: string;
};
export type WaypointBase = {
  waypointId: number;
  coordinates: Coordinates;
  waypointType: WaypointType;
};
export type ViaWaypoint = {
  waypointType: WaypointType.Via;
} & WaypointBase & {
    address?: Address;
  };
export type ChargingStopWaypointSettingsBase = {
  chargingStopWaypointSettingsType: ChargingStopWaypointSettingsType;
};
export type ChargingStopWaypointDurationSetting = {
  chargingStopWaypointSettingsType: ChargingStopWaypointSettingsType.Duration;
} & ChargingStopWaypointSettingsBase & {
    minDuration: number;
    maxDuration: number;
  };
export type ChargingStopWaypointTargetSetting = {
  chargingStopWaypointSettingsType: ChargingStopWaypointSettingsType.Target;
} & ChargingStopWaypointSettingsBase & {
    targetBatteryLevel: number;
  };
export type ChargingStopWaypointSettings =
  | ChargingStopWaypointDurationSetting
  | ChargingStopWaypointTargetSetting;
export type ChargingStopWaypoint = {
  waypointType: WaypointType.ChargingStop;
} & WaypointBase & {
    stationId?: string;
    name?: string;
    address?: Address;
    voltage?: number;
    current?: number;
    power?: number;
    supply_type?: SupplyType;
    settings: ChargingStopWaypointSettings;
    isRestStop?: boolean;
  };
export type StopoverWaypoint = {
  waypointType: WaypointType.Stopover;
} & WaypointBase & {
    name?: string;
    address?: Address;
    duration: number;
    weightDelta: number;
    isRestStop?: boolean;
  };
export type Waypoint = ViaWaypoint | ChargingStopWaypoint | StopoverWaypoint;
export type EvSettings = {
  totalBatteryCapacity?: number;
  initialBatteryCapacity: number;
  maxBatteryCapacity?: number;
  minBatteryCapacity?: number;
  minBatteryCapacityAtDestination?: number;
  auxiliaryConsumption?: number;
  descentRecovery?: number;
  ascentConsumption?: number;
};
export type TourVehicle = {
  vehicleId?: string;
  vehicleProfileId?: string;
  vehicleDeviceId?: string;
  engineType: EngineType;
  vehicleType: VehicleType;
  truckType?: TruckType;
  length: number;
  width: number;
  height: number;
  emptyWeight: number;
  trailerCount: number;
  axlesCount: number;
  trailerAxlesCount: number;
  maxWeightPerAxle: number;
  maxWeightPerAxleGroup?: number;
  maxCargoWeight?: number;
  tunnelCategory?: TunnelCategory;
  plugType?: PlugType[];
  evSettings?: EvSettings;
  batteryCount?: number;
};
export type GeoAreaBase = {
  geoAreaType: GeoAreaType;
};
export type GeoAreaCorridor = {
  geoAreaType: GeoAreaType.Corridor;
} & GeoAreaBase & {
    coordinates: Coordinates[];
    width: number;
  };
export type GeoAreaBoundingBox = {
  geoAreaType: GeoAreaType.BoundingBox;
} & GeoAreaBase & {
    northEast: Coordinates;
    southWest: Coordinates;
  };
export type GeoAreaPolygon = {
  geoAreaType: GeoAreaType.Polygon;
} & GeoAreaBase & {
    coordinates: Coordinates[];
  };
export type GeoArea = GeoAreaCorridor | GeoAreaBoundingBox | GeoAreaPolygon;
export type TourConstraints = {
  areas?: GeoArea[];
  features?: Features[];
  hazardous_classes: HazardousClass[];
};
export type TourCostSettings = {
  vehiclePerKm?: number;
  driverPerHr?: number;
  energyPerKwh?: number;
  currency?: Currency;
};
export type TourEventBase = {
  eventId: string;
  occurredAt: string;
  tourEventType: TourEventType;
};
export type EventCreated = {
  tourEventType: TourEventType.Created;
} & TourEventBase & {
    requestedByVehicle: boolean;
    parentTourId?: string;
  };
export type EventUpdated = {
  tourEventType: TourEventType.Updated;
} & TourEventBase & {
    requestedByVehicle: boolean;
  };
export type EventCancellation = {
  tourEventType: TourEventType.Cancellation;
} & TourEventBase & {
    eventReceived?: string;
    requestedByVehicle: boolean;
  };
export type EventGuidance = {
  tourEventType: TourEventType.Guidance;
} & TourEventBase &
  object;
export type EventSynchronizationStarted = {
  tourEventType: TourEventType.SynchronizationStarted;
} & TourEventBase & {
    vehicleId: string;
    vehicleDeviceId: string;
  };
export type EventSynchronizationUpdated = {
  tourEventType: TourEventType.SynchronizationUpdated;
} & TourEventBase & {
    eventReceived?: string;
    eventSend?: string;
  };
export type TourEvent =
  | EventCreated
  | EventUpdated
  | EventCancellation
  | EventGuidance
  | EventSynchronizationStarted
  | EventSynchronizationUpdated;
export type TourEvents = {
  timelineEvents?: TourEvent[];
};
export type TourDriverConfiguration = {
  /** Remaining driving time in seconds */
  remainingDrivingTime?: number;
  driverId?: string;
};
export type TourResponse = {
  tourId: string;
  parentTourId?: string;
  revision: number;
  name?: string;
  description?: string;
  status: TourStatus;
  departure: TourDeparture;
  arrival: TourArrival;
  waypoints: Waypoint[];
  vehicle: TourVehicle;
  constraints: TourConstraints;
  optimizationMethod?: TourOptimizationMethod;
  createAt: string;
  lastUpdate?: string;
  costSettings: TourCostSettings;
  events?: TourEvents;
  isAutomated: boolean;
  driverConfiguration?: TourDriverConfiguration;
};
export type ItinerarySpan = {
  index?: number;
  ev_consumption: number;
  distance: number;
  duration: number;
  street_attributes?: StreetAttributes[];
  truck_attributes?: TruckAttributes[];
  coordinates?: Coordinates[];
};
export type ItinerarySection = {
  sectionId?: number;
  type?: ItinerarySectionType;
  departureTime?: string;
  arrivalTime?: string;
  distance?: number;
  duration?: number;
  evConsumption?: number;
  evBatteryCapacityDeparture?: number;
  evBatteryCapacityArrival?: number;
  transitMode?: ItinerarySpanTransitMode;
  spans?: ItinerarySpan[];
};
export type ItinerarySummary = {
  distance?: number;
  duration?: number;
  evConsumption?: number;
  evChargingDuration?: number;
  vehicleCost?: number;
  driverCost?: number;
  energyCost?: number;
  minTollCost?: number;
  maxTollCost?: number;
  minTotalCost?: number;
  maxTotalCost?: number;
};
export type TimelineEventBase = {
  eventId: number;
  occurredAt: string;
  timelineEventType: TimelineEventType;
};
export type EventIntermediateStop = {
  timelineEventType: TimelineEventType.IntermediateStop;
} & TimelineEventBase & {
    waypointId: number;
    coordinates: Coordinates;
    address?: Address;
    expectedBatteryLevelAtArrival?: number;
    weightDelta?: number;
    duration: number;
    /** Indicates if this event represents a rest stop */
    isRestStop: boolean;
    /** Duration of rest in seconds (0 if not a rest stop) */
    restDuration: number;
  };
export type Operator = {
  id?: string;
  name?: string;
};
export type EventChargeStop = {
  timelineEventType: TimelineEventType.ChargeStop;
} & TimelineEventBase & {
    waypointId?: number;
    operator?: Operator;
    stationId?: string;
    stationName?: string;
    coordinates: Coordinates;
    address?: Address;
    expectedBatteryLevelAtArrival?: number;
    expectedBatteryLevelAtDeparture?: number;
    minDuration?: number;
    maxDuration?: number;
    voltage?: number;
    current?: number;
    power?: number;
    supply_type?: string;
    plug_type?: string;
    /** Indicates if this event represents a rest stop */
    isRestStop: boolean;
    /** Duration of rest in seconds (0 if not a rest stop) */
    restDuration: number;
  };
export type EventDeparture = {
  timelineEventType: TimelineEventType.Departure;
} & TimelineEventBase & {
    coordinates: Coordinates;
    address?: Address;
    expectedBatteryLevelAtDeparture?: number;
    /** Indicates if this event represents a rest stop */
    isRestStop: boolean;
    /** Duration of rest in seconds (0 if not a rest stop) */
    restDuration: number;
  };
export type EventDriving = {
  timelineEventType: TimelineEventType.Driving;
} & TimelineEventBase & {
    sectionId: number;
  };
export type EventTransit = {
  timelineEventType: TimelineEventType.Transit;
} & TimelineEventBase & {
    sectionId: number;
    boardingCoordinates?: Coordinates;
    deboardingCoordinates?: Coordinates;
  };
export type EventArrival = {
  timelineEventType: TimelineEventType.Arrival;
} & TimelineEventBase & {
    coordinates: Coordinates;
    address?: Address;
    expectedBatteryLevelAtArrival?: number;
    /** Indicates if this event represents a rest stop */
    isRestStop: boolean;
    /** Duration of rest in seconds (0 if not a rest stop) */
    restDuration: number;
  };
export type EventVia = {
  timelineEventType: TimelineEventType.Via;
} & TimelineEventBase & {
    waypointId: number;
    coordinates: Coordinates;
  };
export type TimelineEvent =
  | EventIntermediateStop
  | EventChargeStop
  | EventDeparture
  | EventDriving
  | EventTransit
  | EventArrival
  | EventVia;
export type ItineraryEvents = {
  timelineEvents?: TimelineEvent[];
};
export type ItineraryResponse = {
  tourId?: string;
  itineraryId?: string;
  sections?: ItinerarySection[];
  summary?: ItinerarySummary;
  status?: ItineraryStatus;
  events?: ItineraryEvents;
  source?: ItinerarySource;
};
export type ItinerariesResponse = {
  itineraries?: ItineraryResponse[];
};
export type TourItinerariesResponse = {
  tour?: TourResponse;
  itineraries?: ItinerariesResponse;
};
export type TourRequest = {
  tourId?: string;
  parentTourId?: string;
  name?: string;
  description?: string;
  departure: TourDeparture;
  arrival: TourArrival;
  waypoints: Waypoint[];
  vehicle: TourVehicle;
  constraints: TourConstraints;
  optimizationMethod?: TourOptimizationMethod;
  costSettings: TourCostSettings;
  isAutomated: boolean;
  driverConfiguration?: TourDriverConfiguration;
  routingProvider?: RoutingProvider;
};
export type MmtMessage = {
  messageId?: string;
  tourId?: string;
  accountId?: string;
  parentTourId?: string;
  numberReconstructionAttempt?: number;
  routeIds?: number[];
  ignoreAreas?: string[];
  pointDataItems?: string[];
  routeOptimizationOptions?: string;
  tourName?: string;
  tourHoverName?: string;
  tourState?: string;
  creationEpochMillis?: number;
  lastUpdatedMillis?: number;
  originator?: string;
  originatorId?: string;
  originatorDeviceId?: string;
  originatorGroupId?: string;
  plannedTourStartEpochMillis?: number;
  plannedTourEtaEpochMillis?: number;
  plannedTourDurationSeconds?: number;
  transportOrder?: string;
  srpLevelsEnabled?: number;
  readOnlyLanguageCodeMmtHmi?: string;
  avgEnergyConsumption100Km?: number;
  messageSentDateMillis?: number;
};
export type MmtMessagesResponse = {
  messages?: MmtMessage[];
};
export type ChargingStationMetadata = {
  id?: string;
  tcsId?: string;
  hereId?: string;
  name?: string;
  operator?: Operator;
  location?: Coordinates;
  address?: Address;
  /** The max power that could be transmitted through one of the connectors in kW. */
  maxPower?: number;
  truckClassification?: TruckClassification;
  numberOfConnectors?: number;
  connectorsPowers?: number[];
  tariffIds?: string[];
  connectorsEvseIds?: string[];
  exitPoint?: Coordinates;
  /** Distance in meters from the route exit point to the charging station. */
  distanceFromExitPoint?: number;
  /** Distance in centimeters from the route exit point to the destination. */
  pathPositionCmToDestination?: number;
};
export type ChargingStationMetadataListResponse = {
  hasMore?: boolean;
  chargingStations?: ChargingStationMetadata[];
};
export type RangeFilter = {
  center?: Coordinates;
  radius?: number;
};
export type ChargingStationMetadataRequest = {
  queryType: QueryType;
  bbox?: GeoAreaBoundingBox;
  chargingPower?: ChargingPower[];
  pageSize?: number;
  emspOnly?: boolean;
  truckClassification?: TruckClassification[];
  rangeFilter?: RangeFilter;
  pageNumber?: number;
  ids?: string[];
  coordinate_path?: Coordinates[];
};
export type TimePair = {
  timeTo?: string;
  timeFrom?: string;
};
export type RegularSchedule = {
  dayOfWeek?: DayOfWeek;
  time?: TimePair;
};
export type OpeningHours = {
  open24x7?: boolean;
  chargingWhenClosed?: boolean;
  regularSchedule?: RegularSchedule[];
};
export type ChargePointParkingRestrictions = {
  blockSeveralParkingSpots?: boolean;
  parkingScenario?: ChargePointParkingScenario;
  parkingLotLength?: number;
  parkingLotWidth?: number;
};
export type ChargePointVehicleRestriction = {
  /** Maximum vehicle height, in meters, also known as clearance height */
  heightRestriction?: number;
  /** Maximum vehicle width, in meters */
  widthRestriction?: number;
  /** Maximum vehicle length, in meters */
  lengthRestriction?: number;
  /** Maximum weight for the vehicles entering / parking at the EV charging location, in kilograms */
  weightRestriction?: number;
  /** Indication if entrance with trailers / semi-trailer is permitted at the EV Charging Location */
  canChargeWithTrailer?: boolean;
};
export type ConnectorRate = {
  /** Connector price. */
  price?: string;
  /** Price source */
  contractType?: ContractType;
  tariffId?: string;
  currency?: string;
  currencyDimension?: CurrencyDimension;
};
export type Connector = {
  /** Connector ID */
  id?: string;
  /** EVSE ID */
  evseId?: string;
  standard?: ConnectorStandard;
  plugType?: ConnectorPlugType;
  powerType?: ConnectorPowerType;
  /** Maximum power in kW */
  maxPower?: number;
  truckClassification?: TruckClassification;
  /** Voltage in V */
  voltage?: number;
  /** Connector's amperage in amp */
  current?: number;
  status?: ConnectorStatus;
  paymentMethodSupported?: ConnectorPaymentMethods[];
  prices?: ConnectorRate[];
};
export type ChargePoint = {
  chargePointId?: string;
  /** Shows weather the chargepoint indicates that it supports remote authentication */
  supportsRemoteAuthentication?: boolean;
  parkingRestrictions?: ChargePointParkingRestrictions;
  vehicleRestrictions?: ChargePointVehicleRestriction;
  connectors?: Connector[];
};
export type ChargingStation = {
  id: string;
  tcsId?: string;
  hereId?: string;
  name: string;
  operator: Operator;
  address?: Address;
  location: Coordinates;
  /** The max power that could be transmitted through one of the connectors in kW. */
  maxPower?: number;
  truckClassification?: TruckClassification;
  supportPhone?: string;
  hasADRRestrictions?: boolean;
  hasHazardousGoodsRestrictions?: boolean;
  stationContracts?: StationContracts[];
  amenities?: ChargingStationAmenities[];
  openingHours?: OpeningHours;
  chargePoints?: ChargePoint[];
};
export type VehicleProfileListItem = {
  id: string;
  profileName: string;
  engineType?: EngineType2;
};
export type VehicleProfilePackWeight = {
  packs: number;
  weight: number;
};
export type VehicleProfileDetails = {
  id: string;
  profileName: string;
  vehicleType: VehicleType;
  engineType: EngineType3;
  truckType?: TruckType;
  applicationBodyBuild?: string;
  length: number;
  width: number;
  height: number;
  distanceKingpinRearAxle?: number;
  emptyWeightVehicle: VehicleProfilePackWeight[];
  emptyWeightFreightTrain: VehicleProfilePackWeight[];
  maxFreightTrainWeight: number;
  maxCargoWeight: VehicleProfilePackWeight[];
  maxWeightPerAxle: number;
  maxWeightPerAxleGroup?: number;
  axlesVehicle: number;
  axlesTrailer?: number;
  numberAttachedTrailers: number;
  defaultInstalledBatteryPackCount: number;
  batteryCapacityPerPack: number;
  plugTypes: VehicleProfilePlugTypes[];
};
export type AssetSubscriptionState = {
  /** Actual subscription status */
  status: Status;
  /** Subscription level */
  subscription?: Subscription;
};
export type PermittedAsset = {
  id: string;
  name: string;
  type: string;
  identification?: string;
  identification_type?: string;
  subscription_state?: AssetSubscriptionState;
  group_ids: string[];
  /** Manually calculated field (given the presence of electric related events) */
  details_bev?: boolean;
};
export type PermittedAssetsResponse = {
  items: PermittedAsset[];
};
export type Position = {
  latitude?: number;
  longitude?: number;
  /** Height above sea level in meters */
  altitude?: number;
  /** Speed of the device in Km/h */
  speed?: number;
};
export type State = {
  /** Mileage in Km */
  mileage?: number;
  ignition_on?: boolean;
  /** unit in % */
  state_of_charge?: number;
  /** Remaining range of electric vehicles in Km */
  remaining_range?: number;
  /** Energy level in kWh */
  energy_level?: number;
  /** Fuel level in % */
  fuel_level?: number;
  /** Vehicle total weight in Kg */
  weight_total?: number;
};
export type AssetDetails = {
  /** Possible values - diesel, hydrogen, gas or electric */
  fuel_type?: string;
  number_of_axes?: number;
  /** Information collected about the vehicles in a non defined format */
  tires?: string;
  /** Manually calculated field (given the presence of electric related events) */
  details_bev?: boolean;
};
export type DrivingAndRestingDetails = {
  /** Max remaining time until the next required break (in seconds) */
  remaining_current_driving_time?: number;
};
export type DriverState = {
  driving_and_resting_details?: DrivingAndRestingDetails;
};
export type TemperatureSummary = {
  /** temperature in Celsius */
  temperature?: number;
  /** Temperature description */
  temperature_description?: string;
  /** Highest temperature for the day in Celsius */
  high_temperature?: string;
  /** Lowest temperature for the day in Celsius */
  low_temperature?: string;
};
export type WindSummary = {
  /** Air description */
  air_description?: string;
  /** Wind speed in km/h */
  wind_speed?: number;
  /** Direction of the wind - North */
  wind_description?: string;
  /** Abbreviation of the direction of the wind - N */
  wind_short_description?: string;
};
export type WeatherSummary = {
  /** Weather description */
  description?: string;
  /** Sky weather description */
  sky_description?: string;
  humidity?: string;
  temperature_summary?: TemperatureSummary;
  wind_summary?: WindSummary;
};
export type BatteryConfiguration = {
  batteryPacksNumber?: number;
  chargePortType?: string[];
  maxChargingCurrent?: number;
};
export type AssetDataResponse = {
  /** UUID which represents the asset */
  asset_id: string;
  /** UUID which represents the account */
  account_id: string;
  /** name of the asset */
  name?: string;
  /** asset type (e.g truck, bus, van or trailer) */
  vehicleType?: VehicleType2;
  occurred_at?: string;
  /** Outside temperate provided by HERE weather request */
  temperature?: number;
  position?: Position;
  state?: State;
  details?: AssetDetails;
  driver_state?: DriverState;
  weather?: WeatherSummary;
  batteryConfiguration?: BatteryConfiguration;
};
export type IsolineResponse = {
  points?: Coordinates[];
};
export type MqttReferenceTime = {
  utc?: number;
  powerUpCycles?: number;
  ticks?: number;
};
export type MqttMoMessageHeader = {
  contentID: string;
  messageID: string;
  direction: string;
  source: string;
  gateway: string;
  referenceTime: MqttReferenceTime;
  platformEntryUTC: number;
  device_id?: string;
  asset_id?: string;
  account_id?: string;
};
export type MqttMoMessage = {
  header: MqttMoMessageHeader;
  /** Base64 encoded binary payload */
  payloadRaw: string;
};
export enum TourStatus {
  Draft = "DRAFT",
  Negotiating = "NEGOTIATING",
  Synchronized = "SYNCHRONIZED",
  InProgress = "IN_PROGRESS",
  WaitingUpdateAcceptance = "WAITING_UPDATE_ACCEPTANCE",
  DriverCancelled = "DRIVER_CANCELLED",
  DispatcherCancelled = "DISPATCHER_CANCELLED",
  ProductCancelled = "PRODUCT_CANCELLED",
  Completed = "COMPLETED",
  Replaced = "REPLACED",
}
export enum ErrorMessageErrorCode {
  InternalFailure = "INTERNAL_FAILURE",
  NotFound = "NOT_FOUND",
  InvalidAssetId = "INVALID_ASSET_ID",
  NoSubscriptionFound = "NO_SUBSCRIPTION_FOUND",
  FailedPrecondition = "FAILED_PRECONDITION",
}
export enum WaypointType {
  Via = "VIA",
  ChargingStop = "CHARGING_STOP",
  Stopover = "STOPOVER",
}
export enum SupplyType {
  AcSingle = "AC_SINGLE",
  AcTriple = "AC_TRIPLE",
  Dc = "DC",
}
export enum ChargingStopWaypointSettingsType {
  Duration = "DURATION",
  Target = "TARGET",
}
export enum EngineType {
  Combustion = "COMBUSTION",
  Electric = "ELECTRIC",
}
export enum VehicleType {
  Truck = "TRUCK",
  PrivateBus = "PRIVATE_BUS",
}
export enum TruckType {
  Tractor = "TRACTOR",
  Truck = "TRUCK",
}
export enum TunnelCategory {
  All = "ALL",
  B = "B",
  C = "C",
  D = "D",
  E = "E",
}
export enum PlugType {
  CcsCombo1 = "CCS_COMBO_1",
  CcsCombo2 = "CCS_COMBO_2",
  Mcs = "MCS",
  GbT = "GB_T",
  Mennekes2 = "MENNEKES_2",
  J17721 = "J1772_1",
  Supercharger = "SUPERCHARGER",
  Chademo = "CHADEMO",
}
export enum GeoAreaType {
  Corridor = "CORRIDOR",
  BoundingBox = "BOUNDING_BOX",
  Polygon = "POLYGON",
}
export enum Features {
  ControlledHighway = "CONTROLLED_HIGHWAY",
  TollRoad = "TOLL_ROAD",
  Ferry = "FERRY",
  Tunnel = "TUNNEL",
}
export enum HazardousClass {
  Explosive = "EXPLOSIVE",
  Gas = "GAS",
  FlammableLiquid = "FLAMMABLE_LIQUID",
  FlammableSolid = "FLAMMABLE_SOLID",
  OxidizingAgent = "OXIDIZING_AGENT",
  PoisonousInfectious = "POISONOUS_INFECTIOUS",
  Radioactive = "RADIOACTIVE",
  Corrosive = "CORROSIVE",
  PoisonousInhalation = "POISONOUS_INHALATION",
  HarmfulToWater = "HARMFUL_TO_WATER",
  Organic = "ORGANIC",
}
export enum TourOptimizationMethod {
  Smartest = "SMARTEST",
  Economic = "ECONOMIC",
  Shortest = "SHORTEST",
  Fastest = "FASTEST",
}
export enum Currency {
  Eur = "EUR",
  Bam = "BAM",
  Bgn = "BGN",
  Brl = "BRL",
  Chf = "CHF",
  Czk = "CZK",
  Dkk = "DKK",
  Gbp = "GBP",
  Huf = "HUF",
  Hrk = "HRK",
  Mkd = "MKD",
  Nok = "NOK",
  Pln = "PLN",
  Ron = "RON",
}
export enum TourEventType {
  Created = "CREATED",
  Updated = "UPDATED",
  Cancellation = "CANCELLATION",
  Guidance = "GUIDANCE",
  SynchronizationStarted = "SYNCHRONIZATION_STARTED",
  SynchronizationUpdated = "SYNCHRONIZATION_UPDATED",
}
export enum ItinerarySectionType {
  Driving = "DRIVING",
  Transit = "TRANSIT",
}
export enum ItinerarySpanTransitMode {
  HighSpeedTrain = "HIGH_SPEED_TRAIN",
  IntercityTrain = "INTERCITY_TRAIN",
  InterregionalTrain = "INTERREGIONAL_TRAIN",
  RegionalTrain = "REGIONAL_TRAIN",
  CityTrain = "CITY_TRAIN",
  Bus = "BUS",
  Ferry = "FERRY",
  Subway = "SUBWAY",
  LightRail = "LIGHT_RAIL",
  PrivateBus = "PRIVATE_BUS",
  Inclined = "INCLINED",
  Aerial = "AERIAL",
  BusRapid = "BUS_RAPID",
  Monorail = "MONORAIL",
  CarShuttleTrain = "CAR_SHUTTLE_TRAIN",
  Flight = "FLIGHT",
  Unknown = "UNKNOWN",
}
export enum StreetAttributes {
  RightDrivingSide = "RIGHT_DRIVING_SIDE",
  DirtRoad = "DIRT_ROAD",
  Tunnel = "TUNNEL",
  Bridge = "BRIDGE",
  Ramp = "RAMP",
  Motorway = "MOTORWAY",
  Roundabout = "ROUNDABOUT",
  UnderConstruction = "UNDER_CONSTRUCTION",
  DividedRoad = "DIVIDED_ROAD",
  PrivateRoad = "PRIVATE_ROAD",
  ControlledAccessHighway = "CONTROLLED_ACCESS_HIGHWAY",
  BuiltUpArea = "BUILT_UP_AREA",
}
export enum TruckAttributes {
  Open = "OPEN",
  NoThroughTraffic = "NO_THROUGH_TRAFFIC",
  TollRoad = "TOLL_ROAD",
}
export enum ItineraryStatus {
  Current = "CURRENT",
  Next = "NEXT",
  Draft = "DRAFT",
  Archived = "ARCHIVED",
}
export enum TimelineEventType {
  IntermediateStop = "INTERMEDIATE_STOP",
  ChargeStop = "CHARGE_STOP",
  Via = "VIA",
  Departure = "DEPARTURE",
  Driving = "DRIVING",
  Transit = "TRANSIT",
  Arrival = "ARRIVAL",
}
export enum ItinerarySource {
  Here = "HERE",
  TRoute = "T_ROUTE",
  Other = "OTHER",
}
export enum RoutingProvider {
  Here = "HERE",
  Troute = "TROUTE",
  Both = "BOTH",
}
export enum TruckClassification {
  TruckReady = "TRUCK_READY",
  TruckLimited = "TRUCK_LIMITED",
  TruckIncompatible = "TRUCK_INCOMPATIBLE",
  Unspecified = "UNSPECIFIED",
}
export enum QueryType {
  Bbox = "BBOX",
  Nearest = "NEAREST",
  Cluster = "CLUSTER",
  OnRoute = "ON_ROUTE",
  All = "ALL",
}
export enum ChargingPower {
  Low = "LOW",
  Medium = "MEDIUM",
  High = "HIGH",
}
export enum StationContracts {
  Tcs = "TCS",
  Here = "HERE",
}
export enum ChargingStationAmenities {
  Hotel = "HOTEL",
  Restaurant = "RESTAURANT",
  Cafe = "CAFE",
  Mall = "MALL",
  Supermarket = "SUPERMARKET",
  Sport = "SPORT",
  RecreationArea = "RECREATION_AREA",
  Nature = "NATURE",
  Museum = "MUSEUM",
  BikeSharing = "BIKE_SHARING",
  BusStop = "BUS_STOP",
  TaxiStand = "TAXI_STAND",
  TramStop = "TRAM_STOP",
  MetroStation = "METRO_STATION",
  TrainStation = "TRAIN_STATION",
  Airport = "AIRPORT",
  ParkingLot = "PARKING_LOT",
  CarpoolParking = "CARPOOL_PARKING",
  FuelStation = "FUEL_STATION",
  Wifi = "WIFI",
  Business = "BUSINESS",
  Education = "EDUCATION",
  Finance = "FINANCE",
  Health = "HEALTH",
  Service = "SERVICE",
  Store = "STORE",
  FastFood = "FAST_FOOD",
  Bakery = "BAKERY",
  TruckStop = "TRUCK_STOP",
  Playground = "PLAYGROUND",
  Toilets = "TOILETS",
  Shower = "SHOWER",
  TrailerParking = "TRAILER_PARKING",
  VideoSurveillance = "VIDEO_SURVEILLANCE",
}
export enum DayOfWeek {
  Monday = "MONDAY",
  Tuesday = "TUESDAY",
  Wednesday = "WEDNESDAY",
  Thursday = "THURSDAY",
  Friday = "FRIDAY",
  Saturday = "SATURDAY",
  Sunday = "SUNDAY",
}
export enum ChargePointParkingScenario {
  DriveThrough = "DriveThrough",
  BackwardsDriving = "BackwardsDriving",
  PullThrough = "PullThrough",
  Unknown = "Unknown",
}
export enum ConnectorStandard {
  Iec62196T1 = "Iec62196T1",
  Iec62196T2 = "Iec62196T2",
  Iec62196T3A = "Iec62196T3a",
  Iec603092Three64 = "Iec603092Three64",
  DomesticE = "DomesticE",
  DomesticF = "DomesticF",
  DomesticH = "DomesticH",
  Iec62196T2Combo = "Iec62196T2Combo",
  Iec62196T1Combo = "Iec62196T1Combo",
  Chademo = "Chademo",
  TeslaS = "TeslaS",
  TeslaR = "TeslaR",
  Mcs = "MCS",
  Other = "Other",
  Unknown = "Unknown",
}
export enum ConnectorPlugType {
  Type1Connector = "Type1Connector",
  Type2Connector = "Type2Connector",
  Type2Outlet = "Type2Outlet",
  Type3Outlet = "Type3Outlet",
  TypeEFrenchStandard = "TypeEFrenchStandard",
  TypeFSchuko = "TypeFSchuko",
  Chademo = "Chademo",
  Ccs2 = "CCS2",
  Ccs1 = "CCS1",
  Iec60309ThreePhase = "Iec60309ThreePhase",
  Mcs = "MCS",
  Other = "Other",
  Unknown = "Unknown",
}
export enum ConnectorPowerType {
  Ac = "AC",
  Dc = "DC",
}
export enum ConnectorStatus {
  Available = "Available",
  Faulted = "Faulted",
  Unavailable = "Unavailable",
  Occupied = "Occupied",
  Unknown = "Unknown",
}
export enum ConnectorPaymentMethods {
  ChipCard = "CHIP_CARD",
  ContactlessCard = "CONTACTLESS_CARD",
  CreditCard = "CREDIT_CARD",
  DebitCard = "DEBIT_CARD",
  PedTerminal = "PED_TERMINAL",
  RfidReader = "RFID_READER",
  AuthByCarPlugAndCharge = "AUTH_BY_CAR_PLUG_AND_CHARGE",
  AuthByCarAutoCharge = "AUTH_BY_CAR_AUTO_CHARGE",
  OnlineApplePay = "ONLINE_APPLE_PAY",
  OnlinePayPal = "ONLINE_PAY_PAL",
  OnlineCreditCard = "ONLINE_CREDIT_CARD",
  OnlineGooglePay = "ONLINE_GOOGLE_PAY",
  OnlineBankPayment = "ONLINE_BANK_PAYMENT",
  TerminalQrCode = "TERMINAL_QR_CODE",
  TerminalSms = "TERMINAL_SMS",
  OperatorApp = "OPERATOR_APP",
  MobilePayment = "MOBILE_PAYMENT",
  Other = "OTHER",
}
export enum ContractType {
  Tcs = "TCS",
  Here = "HERE",
}
export enum CurrencyDimension {
  Energy = "ENERGY",
  Time = "TIME",
  Flat = "FLAT",
  ParkingTime = "PARKING_TIME",
}
export enum EngineType2 {
  Bev = "BEV",
  Ice = "ICE",
}
export enum EngineType3 {
  Combustion = "COMBUSTION",
  Electric = "ELECTRIC",
}
export enum VehicleProfilePlugTypes {
  Ccs = "CCS",
  Mcs = "MCS",
}
export enum Status {
  Active = "ACTIVE",
  PendingActivation = "PENDING_ACTIVATION",
  PendingCancellation = "PENDING_CANCELLATION",
  Inactive = "INACTIVE",
}
export enum Subscription {
  SrpL10 = "SRP_L10",
  SrpL10Ft = "SRP_L10_FT",
  SrpL11 = "SRP_L11",
  SrpL12 = "SRP_L12",
  SrpL13 = "SRP_L13",
  SrpL14 = "SRP_L14",
  SrpL15 = "SRP_L15",
  NoLevel = "NO_LEVEL",
}
export enum VehicleType2 {
  Truck = "TRUCK",
  Bus = "BUS",
  Van = "VAN",
  Trailer = "TRAILER",
  Undefined = "UNDEFINED",
}
export enum IsolineMode {
  Distance = "DISTANCE",
  Time = "TIME",
  Consumption = "CONSUMPTION",
}
export const {
  useGetToursQuery,
  useLazyGetToursQuery,
  useCreateTourMutation,
  useGetTourQuery,
  useLazyGetTourQuery,
  useUpdateTourMutation,
  useCanceledTourMutation,
  useSendTourToVehicleMutation,
  useSendTourToOptimizerMutation,
  useFetchTourItinerariesQuery,
  useLazyFetchTourItinerariesQuery,
  useGetMmtMessagesQuery,
  useLazyGetMmtMessagesQuery,
  useGetChargingStationMetadataListMutation,
  useGetChargingStationQuery,
  useLazyGetChargingStationQuery,
  useGetVehicleProfileListQuery,
  useLazyGetVehicleProfileListQuery,
  useGetVehicleProfileDetailsQuery,
  useLazyGetVehicleProfileDetailsQuery,
  useGetPermittedAssetsQuery,
  useLazyGetPermittedAssetsQuery,
  useGetAssetDataQuery,
  useLazyGetAssetDataQuery,
  useGenerateIsolineQuery,
  useLazyGenerateIsolineQuery,
  useSendMmtMessageMutation,
  useGetAssetSubscriptionStateQuery,
  useLazyGetAssetSubscriptionStateQuery,
} = injectedRtkApi;
